import React from 'react';
import {
    Box,
    Container,
    Grid,
    Typography,
    Button,
    IconButton,
    Divider,
    Link,
    useTheme,
    alpha,
} from '@mui/material';
import {
    Facebook as FacebookIcon,
    Twitter as TwitterIcon,
    LinkedIn as LinkedInIcon,
    YouTube as YouTubeIcon,
    Apple as AppleIcon,
    Android as AndroidIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    LocationOn as LocationIcon,
    ArrowForward as ArrowIcon,
    Info as AboutIcon,
    DesignServices as ServicesIcon,
    Folder as CaseStudiesIcon,
    Article as BlogIcon,
    ContactMail as ContactIcon,
    Groups as CommunityIcon,
    Book as ResourcesIcon,
    Help as FaqsIcon,
    Policy as PrivacyIcon,
    Work as CareersIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import logo from '../../images/espayLogo.png';

const Footer = () => {
    const theme = useTheme();

    const companyLinks = [
        { label: 'About Us', href: '/about', icon: <AboutIcon sx={{ fontSize: 18 }} /> },
        { label: 'Services', href: '/services', icon: <ServicesIcon sx={{ fontSize: 18 }} /> },
        { label: 'Case Studies', href: '/case-studies', icon: <CaseStudiesIcon sx={{ fontSize: 18 }} /> },
        { label: 'Blog', href: '/blog', icon: <BlogIcon sx={{ fontSize: 18 }} /> },
        { label: 'Contact', href: '/contact', icon: <ContactIcon sx={{ fontSize: 18 }} /> }
    ];

    const supportLinks = [
        { label: 'Community', href: '/community', icon: <CommunityIcon sx={{ fontSize: 18 }} /> },
        { label: 'Resources', href: '/resources', icon: <ResourcesIcon sx={{ fontSize: 18 }} /> },
        { label: 'FAQs', href: '/faqs', icon: <FaqsIcon sx={{ fontSize: 18 }} /> },
        { label: 'Privacy Policy', href: '/privacy', icon: <PrivacyIcon sx={{ fontSize: 18 }} /> },
        { label: 'Careers', href: '/careers', icon: <CareersIcon sx={{ fontSize: 18 }} /> }
    ];

    const socialMedia = [
        { icon: <FacebookIcon />, href: 'https://facebook.com', color: '#1877F2' },
        { icon: <TwitterIcon />, href: 'https://twitter.com', color: '#1DA1F2' },
        { icon: <LinkedInIcon />, href: 'https://linkedin.com', color: '#0077B5' },
        { icon: <YouTubeIcon />, href: 'https://youtube.com', color: '#FF0000' }
    ];

    const contactInfo = [
        {
            icon: <LocationIcon sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                p: 0.5,
                borderRadius: '50%'
            }} />,
            text: 'Kalimati, Kathmandu'
        },
        {
            icon: <PhoneIcon sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                p: 0.5,
                borderRadius: '50%'
            }} />,
            text: '977-1-5165236, 977-9803344239'
        },
        {
            icon: <EmailIcon sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.2),
                p: 0.5,
                borderRadius: '50%'
            }} />,
            text: 'easysoftwarenepal@gmail.com'
        }
    ];

    // Animation variants
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { y: 20, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.5
            }
        }
    };

    return (
        <Box
            component="footer"
            sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${alpha(theme.palette.primary.main, 0.95)} 100%)`,
                color: 'white',
                pt: 8,
                pb: 4,
                position: 'relative',
                overflow: 'hidden',
                '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                    opacity: 0.8
                },
                '&:after': {
                    content: '""',
                    position: 'absolute',
                    bottom: -50,
                    right: -50,
                    width: 200,
                    height: 200,
                    borderRadius: '50%',
                    background: alpha(theme.palette.primary.light, 0.1),
                    zIndex: 0
                }
            }}
        >
            {/* Additional decorative elements */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 100,
                    left: -50,
                    width: 150,
                    height: 150,
                    borderRadius: '50%',
                    background: alpha(theme.palette.secondary.main, 0.1),
                    zIndex: 0
                }}
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                >
                    <Grid container spacing={4}>
                        {/* Company Info */}
                        <Grid item xs={12} md={4}>
                            <motion.div variants={itemVariants}>
                                <Box>
                                    <Box
                                        component="img"
                                        src={logo}
                                        alt="Easy Software"
                                        sx={{
                                            height: 70,
                                            mb: 3,
                                        }}
                                    />
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            mb: 3,
                                            color: 'white',
                                            opacity: 0.9,
                                            lineHeight: 1.6,
                                            textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                        }}
                                    >
                                        Easy Software Pvt ltd aims to provide creative and easy software for companies and other association.
                                    </Typography>

                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mb: 3,
                                        cursor: 'pointer',
                                        '&:hover > .MuiTypography-root': {
                                            transform: 'translateX(4px)'
                                        }
                                    }}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: 'bold',
                                                mr: 1,
                                                color: 'white',
                                                transition: 'transform 0.3s ease'
                                            }}
                                        >
                                            Read More
                                        </Typography>
                                        <motion.div
                                            animate={{ x: [0, 5, 0] }}
                                            transition={{ duration: 1.5, repeat: Infinity }}
                                        >
                                            <ArrowIcon sx={{ fontSize: 16 }} />
                                        </motion.div>
                                    </Box>

                                    {/* Social Media */}
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {socialMedia.map((social, index) => (
                                            <motion.div
                                                key={index}
                                                whileHover={{ scale: 1.1, y: -5 }}
                                                whileTap={{ scale: 0.95 }}
                                                transition={{ type: "spring", stiffness: 400, damping: 17 }}
                                            >
                                                <IconButton
                                                    href={social.href}
                                                    target="_blank"
                                                    sx={{
                                                        backgroundColor: alpha('#fff', 0.1),
                                                        color: 'white',
                                                        '&:hover': {
                                                            backgroundColor: social.color,
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: `0 6px 12px ${alpha(social.color, 0.4)}`
                                                        },
                                                        transition: 'all 0.3s ease',
                                                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                                    }}
                                                >
                                                    {social.icon}
                                                </IconButton>
                                            </motion.div>
                                        ))}
                                    </Box>
                                </Box>
                            </motion.div>
                        </Grid>

                        {/* Company Links */}
                        <Grid item xs={12} sm={6} md={2}>
                            <motion.div variants={itemVariants}>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 'bold',
                                            mb: 3,
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                                        }}
                                    >
                                        <AboutIcon sx={{ mr: 1, }} />
                                        Company
                                    </Typography>
                                    {companyLinks.map((link, index) => (
                                        <motion.div
                                            key={index}
                                            whileHover={{ x: 5 }}
                                            transition={{ type: "spring", stiffness: 400, damping: 10 }}
                                        >
                                            <Link
                                                href={link.href}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    color: alpha('#fff', 0.8),
                                                    mb: 2,
                                                    textDecoration: 'none',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        color: 'white',
                                                    }
                                                }}
                                            >
                                                <Box sx={{ mr: 1, display: 'flex' }}>
                                                    {link.icon}
                                                </Box>
                                                {link.label}
                                            </Link>
                                        </motion.div>
                                    ))}
                                </Box>
                            </motion.div>
                        </Grid>

                        {/* Support Links */}
                        <Grid item xs={12} sm={6} md={2}>
                            <motion.div variants={itemVariants}>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 'bold',
                                            mb: 3,
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                                        }}
                                    >
                                        <CommunityIcon sx={{ mr: 1, }} />
                                        Support
                                    </Typography>
                                    {supportLinks.map((link, index) => (
                                        <motion.div
                                            key={index}
                                            whileHover={{ x: 5 }}
                                            transition={{ type: "spring", stiffness: 400, damping: 10 }}
                                        >
                                            <Link
                                                href={link.href}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    color: alpha('#fff', 0.8),
                                                    mb: 2,
                                                    textDecoration: 'none',
                                                    transition: 'all 0.3s ease',
                                                    '&:hover': {
                                                        color: 'white',
                                                    }
                                                }}
                                            >
                                                <Box sx={{ mr: 1, display: 'flex' }}>
                                                    {link.icon}
                                                </Box>
                                                {link.label}
                                            </Link>
                                        </motion.div>
                                    ))}
                                </Box>
                            </motion.div>
                        </Grid>

                        {/* Contact Info */}
                        <Grid item xs={12} md={4}>
                            <motion.div variants={itemVariants}>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight: 'bold',
                                            mb: 3,
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            textShadow: '0 1px 2px rgba(0,0,0,0.2)'
                                        }}
                                    >
                                        <ContactIcon sx={{ mr: 1, }} />
                                        Contact Us
                                    </Typography>

                                    {contactInfo.map((contact, index) => (
                                        <motion.div
                                            key={index}
                                            variants={itemVariants}
                                            whileHover={{ x: 5 }}
                                            transition={{ type: "spring", stiffness: 400, damping: 10 }}
                                        >
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5 }}>
                                                {contact.icon}
                                                <Typography variant="body2" sx={{ color: 'white', ml: 2 }}>
                                                    {contact.text}
                                                </Typography>
                                            </Box>
                                        </motion.div>
                                    ))}

                                    {/* Download Buttons */}
                                    <motion.div variants={itemVariants}>
                                        <Typography variant="body2" sx={{ mb: 2, fontWeight: 'medium', color: 'white' }}>
                                            Download Our App
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                                            <motion.div whileHover={{ y: -5 }} whileTap={{ scale: 0.95 }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<AppleIcon />}
                                                    sx={{
                                                        backgroundColor: 'black',
                                                        color: 'white',
                                                        borderRadius: 2,
                                                        px: 2,
                                                        py: 1,
                                                        '&:hover': {
                                                            backgroundColor: '#333',
                                                            boxShadow: '0 6px 12px rgba(0,0,0,0.2)'
                                                        },
                                                        transition: 'all 0.3s ease',
                                                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                                                    }}
                                                >
                                                    iOS App
                                                </Button>
                                            </motion.div>
                                            <motion.div whileHover={{ y: -5 }} whileTap={{ scale: 0.95 }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<AndroidIcon />}
                                                    sx={{
                                                        backgroundColor: theme.palette.success.main,
                                                        color: 'white',
                                                        borderRadius: 2,
                                                        px: 2,
                                                        py: 1,
                                                        '&:hover': {
                                                            backgroundColor: theme.palette.success.dark,
                                                            boxShadow: `0 6px 12px ${alpha(theme.palette.success.main, 0.4)}`
                                                        },
                                                        transition: 'all 0.3s ease',
                                                        boxShadow: `0 4px 8px ${alpha(theme.palette.success.main, 0.2)}`
                                                    }}
                                                >
                                                    Android App
                                                </Button>
                                            </motion.div>
                                        </Box>
                                    </motion.div>
                                </Box>
                            </motion.div>
                        </Grid>
                    </Grid>

                    <Divider sx={{
                        my: 4,
                        backgroundColor: alpha('#fff', 0.2),
                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                    }} />

                    {/* Copyright */}
                    <motion.div
                        variants={itemVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', md: 'row' },
                                justifyContent: 'center',
                                alignItems: 'center',
                                textAlign: 'center'
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{
                                    opacity: 0.8,
                                    mb: { xs: 2, md: 0 },
                                    color: 'white',
                                    textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                }}
                            >
                                Copyright © 2022 EasySoftware. All rights reserved.
                            </Typography>
                        </Box>
                    </motion.div>
                </motion.div>
            </Container>
        </Box>
    );
};

export default Footer;